import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { storageService } from '@/services/storageService'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'

interface PromptHistoryItem {
  id: string
  original_prompt: string
  created_at: string
}

interface PromptHistoryProps {
  onClose?: () => void
}

const PromptHistory: React.FC<PromptHistoryProps> = ({ onClose }) => {
  const { t } = useTranslation('common')
  const [prompts, setPrompts] = useState<PromptHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user, isGuest } = useAuth()
  const navigate = useLocalizedNavigate()

  useEffect(() => {
    // Load history for both authenticated users and guest mode
      loadPromptHistory()
  }, [user, isGuest])

  const loadPromptHistory = async () => {
    setLoading(true)
    setError(null)

    try {
      const allPrompts = await storageService.getAllPrompts(user)
      // Map to the expected format
      const formattedPrompts = allPrompts.map(p => ({
        id: p.id,
        original_prompt: p.original_prompt,
        created_at: p.created_at
      }))
      setPrompts(formattedPrompts)
    } catch (err) {
      console.error('Error loading prompt history:', err)
      setError(t('promptHistory.error'))
    } finally {
      setLoading(false)
    }
  }

  const handlePromptClick = (promptId: string) => {
    navigate('/refine', { state: { promptId } })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 24) {
      return t('promptHistory.hoursAgo', { count: diffInHours })
    } else if (diffInHours < 168) { // 7 days
      const diffInDays = Math.floor(diffInHours / 24)
      return t('promptHistory.daysAgo', { count: diffInDays })
    } else {
      return date.toLocaleDateString()
    }
  }

  const truncatePrompt = (prompt: string, maxLength: number = 100) => {
    if (prompt.length <= maxLength) return prompt
    return prompt.substring(0, maxLength) + '...'
  }

  // Hide prompt history in guest mode (iframe)
  if (isGuest) {
    return null
  }

  if (!user && !isGuest) {
    return null
  }

  return (
    <div className="w-full sm:w-80 bg-white border-r border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('promptHistory.title')}</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            aria-label={t('promptHistory.closeAriaLabel')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">{t('promptHistory.loading')}</p>
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <Button
              onClick={loadPromptHistory}
              variant="outline"
              size="sm"
              className="mt-2"
            >
              {t('promptHistory.retry')}
            </Button>
          </div>
        ) : prompts.length === 0 ? (
          <div className="p-4 text-center">
            <p className="text-sm text-gray-500">{t('promptHistory.empty')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('promptHistory.emptyHelp')}</p>
          </div>
        ) : (
          <div className="p-2">
            {prompts.map((prompt) => (
              <button
                key={prompt.id}
                onClick={() => handlePromptClick(prompt.id)}
                className="w-full p-2 sm:p-3 text-left hover:bg-gray-50 rounded-lg transition-colors group"
              >
                <div className="space-y-1">
                  <p className="text-xs sm:text-sm text-gray-900 group-hover:text-gray-700 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {truncatePrompt(prompt.original_prompt)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-400">
                    {formatDate(prompt.created_at)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default PromptHistory
